/**
 * Auction Auto-Fill Script
 * Reads bid_amount from URL and auto-fills the Auction Today bid form on product pages
 */

(function () {
    'use strict';

    function getUrlParams() {
        const params = new URLSearchParams(window.location.search);
        return {
            bidAmount: params.get('bid_amount'),
            autoBid: params.get('auto_bid') === '1',
            variant: params.get('variant')
        };
    }

    /**
     * Resolve Product ID for VSOT lookups
     */
    function resolveProductId() {
        try {
            if (typeof ShopifyAnalytics !== 'undefined' && ShopifyAnalytics.meta?.product?.id) {
                return String(ShopifyAnalytics.meta.product.id);
            }
            if (typeof meta !== 'undefined' && meta && meta.product && meta.product.id) {
                return String(meta.product.id);
            }
            const ogId = document.querySelector('meta[property="og:id"]');
            if (ogId && ogId.content && ogId.content.includes('/')) {
                return ogId.content.split('/').pop();
            }
        } catch (e) { }
        return null;
    }

    /**
     * Get increment from DOM-authoritative state
     * (Core logic in auction-card-info.js now populates these attributes from server API)
     */
    function getResolvedIncrement(input, currentBid) {
        try {
            // Find the parent auction info card
            const card = input.closest('.auction-card-info') || document.querySelector('.auction-card-info');
            if (card) {
                const auctionId = card.getAttribute('data-auction-id');
                const productId = card.getAttribute('data-product-id');

                // Rely on the core logic's applyBiddingRules which is now globally available
                if (typeof window.applyBiddingRules === 'function') {
                    const nextMin = window.applyBiddingRules(currentBid, auctionId, productId);
                    const diff = nextMin - currentBid;
                    if (diff > 0) return diff;
                }
            }
        } catch (e) { }
        return 1;
    }

    /**
     * Get Auction ID from widget structure
     */
    function getAuctionId(widget) {
        if (!widget) return null;
        const wkDiv = widget.querySelector('[class*="wk-auction-"]');
        if (wkDiv) {
            const match = wkDiv.className.match(/wk-auction-(\d+)/);
            if (match) return match[1];
        }
        const hiddenInput = widget.querySelector('input#auction_id, input[name="auction_id"], input[name="wk_auction_id"]');
        if (hiddenInput && hiddenInput.value) return hiddenInput.value;
        const fallback = widget.innerHTML.match(/wk-auction-(\d+)/);
        return fallback ? fallback[1] : null;
    }

    /**
     * Enhance the bid input with +/- buttons (Stepper Design)
     */
    function enhanceBidInput(input) {
        if (!input || input.hasAttribute('data-enhanced')) return;

        // Create the stepper structure
        const stepper = document.createElement('div');
        stepper.className = 'auction-card-info__stepper auction-product-page-stepper';

        const minusBtn = document.createElement('button');
        minusBtn.type = 'button';
        minusBtn.className = 'auction-card-info__step-button auction-card-info__step-button--minus';
        minusBtn.innerHTML = '';

        const container = document.createElement('div');
        container.className = 'auction-card-info__input-container';

        const symbol = document.createElement('span');
        symbol.className = 'auction-card-info__currency-symbol';
        symbol.textContent = '$';

        const plusBtn = document.createElement('button');
        plusBtn.type = 'button';
        plusBtn.className = 'auction-card-info__step-button auction-card-info__step-button--plus';
        plusBtn.innerHTML = '';

        // Wrap the input
        input.parentNode.insertBefore(stepper, input);
        stepper.appendChild(minusBtn);
        stepper.appendChild(container);
        container.appendChild(symbol);
        container.appendChild(input);
        stepper.appendChild(plusBtn);

        // Add class to original input for styling
        input.classList.add('auction-card-info__input');
        input.setAttribute('data-enhanced', 'true');

        // Add event listeners
        minusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const currentVal = parseFloat(input.value) || 0;
            const minAttr = input.getAttribute('min');
            const minVal = minAttr !== null ? parseFloat(minAttr) : 0;

            const increment = getResolvedIncrement(input, currentVal - 1);

            if (currentVal > minVal) {
                input.value = (currentVal - increment).toFixed(0);
                if (parseFloat(input.value) < minVal) input.value = minVal.toFixed(0);
                input.dispatchEvent(new Event('input', { bubbles: true }));
                input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });

        plusBtn.addEventListener('click', (e) => {
            e.preventDefault();
            const currentVal = parseFloat(input.value) || 0;
            const increment = getResolvedIncrement(input, currentVal);

            input.value = (currentVal + increment).toFixed(0);
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        console.log('[Auction Auto-Fill] Bid input enhanced with stepper');
    }

    /**
     * Try to find and fill the Auction Today bid input
     */
    function fillBidForm(bidAmount) {
        // Common selectors for Auction Today bid input fields
        const selectors = [
            '#codeBlock-auction-input-element',
            'input[name="bid_amount"]',
            'input[name="bidAmount"]',
            'input#bid_amount',
            '#bid_amount',
            '.auction-bid-input',
            '.bid-input',
            '[data-auction-bid-input]',
            'input[type="number"][placeholder*="bid" i]',
            'input[type="number"][placeholder*="amount" i]',
            '.auction-today input[type="number"]',
            '#bid-amount',
            '#bidAmount'
        ];

        let foundInput = null;
        for (const selector of selectors) {
            const inputs = document.querySelectorAll(selector);
            if (inputs.length > 0) {
                inputs.forEach(input => {
                    // SCOPING FIX: 
                    // On Product Pages, prevent this script from hijacking inputs in "You may also like" (recommendations).
                    // We only want to auto-fill the MAIN product's bid form or the "Auction Today" widget on home.
                    const isProductPage = window.location.pathname.includes('/products/');
                    const isInsideCard = input.closest('.product-card, .grid__item, .card, 	product-card');

                    if (isProductPage && isInsideCard) {
                        // Skip recommendation cards on PDP
                        return;
                    }

                    foundInput = input;
                    if (bidAmount) {
                        input.value = bidAmount;
                        // Also update the min attribute if possible to prevent validation errors
                        input.setAttribute('min', bidAmount);
                    }
                    // Enhance with stepper
                    enhanceBidInput(input);

                    // Trigger events
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                });
                if (bidAmount) console.log('[Auction Auto-Fill] Bid amount pre-filled:', bidAmount);
                return true;
            }
        }
        return false;
    }

    /**
     * Try to discover the minimum bid amount from the page text
     */
    function discoverMinBid() {
        function discoverMinBid() {
            const productId = resolveProductId();
            // Improve selector: STRICTLY prefer #wk-auction (Main Product) 
            // to avoid picking up .wk_auction_container from "You may also like" (Related Products)
            const pdpWidget = document.querySelector('#wk-auction') || document.querySelector('.wk_auction_container');

            // Scope auction ID discovery to the specific widget
            const auctionId = pdpWidget ? (pdpWidget.getAttribute('data-auction-id') || (pdpWidget.querySelector('#auction_id') ? pdpWidget.querySelector('#auction_id').value : null)) : null;

            // STRICT API ENFORCEMENT: 
            // Do NOT scrape the DOM for "Minimum bidding amount" text.
            // This causes the $161 issue if a related product (Yellow Submarine) is on the page.
            // We must rely ONLY on window.applyBiddingRules which uses the validated API data.

            /*
            // 1. Try DOM state (populated by auction-card-info.js from API)
            const selectorsToTry = [
                '.auction-card-info[data-min-bid]',
                '#wk-auction[data-min-bid]',
                '.wk_auction_container[data-min-bid]'
            ];
            // ... scraping logic commented out ...
            */

            // If we have the API-driven logic available, use it to calculate the TRUE next bid
            if (typeof window.applyBiddingRules === 'function' && auctionId) {
                // We need the CURRENT bid to calculate the next one.
                // We can scrape the *current* bid safely if we scope it to the main widget
                // We can scrape the *current* bid safely if we scope it to the main widget
                const currentBidInput = pdpWidget ? pdpWidget.querySelector('#current_bid_hidden') : null;
                const currentBidEl = pdpWidget ? pdpWidget.querySelector('#current_bid, .current_bid') : null;

                let currentBid = 0;
                if (currentBidInput && currentBidInput.value) {
                    currentBid = parseFloat(currentBidInput.value.replace(/[^0-9.]/g, ''));
                } else if (currentBidEl) {
                    currentBid = parseFloat(currentBidEl.textContent.replace(/[^0-9.]/g, ''));
                }
                if (!isNaN(currentBid)) {
                    const nextBid = window.applyBiddingRules(currentBid, auctionId, productId);
                    console.log('[Auction Auto-Fill] Calculated authoritative next bid:', nextBid);
                    return nextBid;
                }
            }
        }

        return null;
    }

    /**
     * Scroll to the bid form for visibility
     */
    function scrollToBidForm() {
        const selectors = [
            '#codeBlock-auction-widget',
            '.auction-today',
            '[data-auction]',
            '.auction-widget',
            '.bid-form',
            'form[action*="bid"]'
        ];

        for (const selector of selectors) {
            const element = document.querySelector(selector);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return true;
            }
        }
        return false;
    }

    /**
     * Show notification to user
     */
    function showNotification(message) {
        const notification = document.createElement('div');
        notification.className = 'auction-autofill-notification';
        notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ffffff;
        color: #000;
        padding: 16px 24px;
        border-radius: 12px;
        font-weight: 700;
        font-size: 14px;
        z-index: 9999;
        box-shadow: 0 4px 20px rgba(0,0,0,0.25);
        animation: slideIn 0.3s ease-out;
        border: 2px solid #000;
      ">
        ${message}
      </div>
      <style>
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      </style>
    `;
        document.body.appendChild(notification);

        // Auto-remove after 4 seconds
        setTimeout(() => {
            notification.remove();
        }, 4000);
    }

    /**
     * Card-ify the auction widget for a premium look
     */
    function cardifyWidget() {
        const selectors = [
            '#codeBlock-auction-widget',
            '.auction-today',
            '.at-auction-container',
            '[data-auction]',
            '.auction-widget'
        ];

        for (const selector of selectors) {
            const element = document.querySelector(selector);
            if (element && !element.classList.contains('auction-card-ified')) {
                element.classList.add('auction-card-ified');
                console.log('[Auction Auto-Fill] Widget card-ified:', selector);
                return true;
            }
        }
        return false;
    }

    /**
     * Main initialization
     */
    function init() {
        const params = getUrlParams();
        const hasBidParam = params.bidAmount && !isNaN(parseFloat(params.bidAmount));

        // Attempt to card-ify the widget always
        cardifyWidget();

        // Always try to enhance the form, even if no bid_amount param
        let finalBid = hasBidParam ? params.bidAmount : discoverMinBid();
        let filled = fillBidForm(finalBid);

        if (filled) {
            if (hasBidParam) {
                scrollToBidForm();
                showNotification(`Bid amount $${parseFloat(params.bidAmount || '0').toFixed(2)} pre-filled. Confirm your bid below.`);
            } else if (finalBid) {
                console.log('[Auction Auto-Fill] Auto-discovered min bid:', finalBid);
            }
        } else {
            // Wait for Auction Today widget to load
            let attempts = 0;
            const maxAttempts = 60; // Increased attempts
            const interval = 250;

            const checkAndFill = () => {
                attempts++;

                // Try card-ifying and filling
                let currentBid = hasBidParam ? params.bidAmount : discoverMinBid();
                filled = fillBidForm(currentBid);

                if (filled) {
                    if (hasBidParam) {
                        scrollToBidForm();
                        showNotification(`Bid amount $${parseFloat(params.bidAmount || '0').toFixed(2)} pre-filled. Confirm your bid below.`);
                    } else if (currentBid) {
                        console.log('[Auction Auto-Fill] Auto-discovered min bid:', currentBid);
                    }
                } else if (attempts < maxAttempts) {
                    setTimeout(checkAndFill, interval);
                }
            };

            setTimeout(checkAndFill, interval);
        }

        // Clean up URL to remove bid parameters
        if (hasBidParam && window.history && window.history.replaceState) {
            const cleanUrl = new URL(window.location.href);
            cleanUrl.searchParams.delete('bid_amount');
            cleanUrl.searchParams.delete('auto_bid');
            window.history.replaceState({}, '', cleanUrl.toString());
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        // Run immediately and also in a bit to be sure
        init();
        setTimeout(init, 500);
        setTimeout(init, 2000);
    }
})();
